/* eslint-disable jsdoc/require-jsdoc, jsdoc/require-returns, jsdoc/require-param */
import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';
import { getUserNameFromSessionStorage } from '../services/session-storage-service';
import { openBleStream, StreamStatus } from '../services/ble-live-stream';
import LiveLineChart, { LivePoint, LiveSeries } from '../components/BleLive/LiveLineChart';
import LiveTrackMap, { Track } from '../components/BleLive/LiveTrackMap';
import ParameterBoxPlot, {
  computeStats,
  fmt,
  ParameterStats
} from '../components/BleLive/ParameterBoxPlot';
import { CARD_CLASS } from '../components/ParticleBins/particleBinShared';

const ADMIN_EMAIL = 'suresh@potsdamsensors.com';
const BASE = `${DEV_NEW_DOMAIN_URL}/ble-pings/admin`;

/** Most rows kept in memory, whatever the window — bounds a tab left open all day. */
const MAX_ROWS = 20000;
/** History loaded on open and after a stream gap (the API's own max). */
const HISTORY_LIMIT = 500;
/** Redraw at most this often, however fast pings arrive. */
const RENDER_INTERVAL_MS = 250;

const WINDOWS = [
  { key: 5, label: '5 min' },
  { key: 15, label: '15 min' },
  { key: 60, label: '1 hour' }
];

/** "Go back in time" ranges, ending now. */
const HISTORY_PRESETS = [
  { key: '1h', label: '1 hour', ms: 3600000 },
  { key: '6h', label: '6 hours', ms: 6 * 3600000 },
  { key: '24h', label: '24 hours', ms: 24 * 3600000 },
  { key: '7d', label: '7 days', ms: 7 * 24 * 3600000 }
];
/** The server rejects longer ranges. */
const MAX_HISTORY_MS = 31 * 24 * 3600000;

const PALETTE = [
  '#2E90FA',
  '#F79009',
  '#12B76A',
  '#EE46BC',
  '#7A5AF8',
  '#F04438',
  '#06AED4',
  '#EAAA08'
];
const TRACK_COLOR = '#2E90FA';

type PingValue = number | string | boolean | null;

type BlePing = {
  id: number;
  deviceSerial: number;
  deviceName: string | null;
  observedAt: number;
  values: Record<string, PingValue> | null;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  accelX: number | null;
  accelY: number | null;
  accelZ: number | null;
};

type BleDevice = {
  deviceSerial: number;
  deviceName: string | null;
  lastSeenAt: number;
  pingCount: number;
};

type HistoryPoint = {
  t: number;
  n: number;
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  accelX: number | null;
  accelY: number | null;
  accelZ: number | null;
  accelPeak: number | null;
  values: Record<string, number>;
};

type HistoryStats = Omit<ParameterStats, 'latest'>;

type HistoryData = {
  deviceSerial: number;
  from: number;
  to: number;
  bucketMs: number;
  rawCount: number;
  points: HistoryPoint[];
  stats: Record<string, HistoryStats>;
};

type Mode = 'live' | 'history';

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function statusPill(status: StreamStatus): { label: string; className: string } {
  switch (status) {
    case 'live':
      return {
        label: 'Live',
        className: 'border-success-500/50 bg-success-500/15 text-success-400'
      };
    case 'connecting':
      return { label: 'Connecting…', className: 'border-gray-600 bg-gray-500/15 text-gray-300' };
    case 'reconnecting':
      return {
        label: 'Reconnecting…',
        className: 'border-warning-500/50 bg-warning-500/15 text-warning-300'
      };
    case 'denied':
      return {
        label: 'Not authorised',
        className: 'border-error-500/50 bg-error-500/15 text-error-400'
      };
    default:
      return { label: 'Offline', className: 'border-gray-600 bg-gray-500/15 text-gray-400' };
  }
}

function ageLabel(ms: number): string {
  if (ms < 2000) return 'just now';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  return m < 60 ? `${m}m ago` : `${Math.round(m / 60)}h ago`;
}

/** "3 s", "2 min", "1 h" — the width of one averaged history point. */
function bucketLabel(ms: number): string {
  if (ms < 60000) return `${Math.round(ms / 1000)} s`;
  if (ms < 3600000) return `${Math.round(ms / 60000)} min`;
  return `${Math.round(ms / 3600000)} h`;
}

function spanLabel(ms: number): string {
  const h = ms / 3600000;
  if (h < 1) return `${Math.round(ms / 60000)} min`;
  if (h < 48) return `${Math.round(h)} h`;
  return `${Math.round(h / 24)} days`;
}

/** Value for a datetime-local input, in the browser's timezone. */
function toLocalInput(ms: number): string {
  const d = new Date(ms - new Date(ms).getTimezoneOffset() * 60000);
  return d.toISOString().slice(0, 16);
}

function points(rows: BlePing[], pick: (p: BlePing) => number | null): LivePoint[] {
  const out: LivePoint[] = [];
  rows.forEach((p) => {
    const y = pick(p);
    if (isNum(y)) out.push({ x: p.observedAt, y });
  });
  return out;
}

/**
 * Admin-only real-time graphs for ONE BLE device: every numeric parameter the
 * sensor sends (live line + window distribution), then the relaying phone's
 * GPS track, accelerometer and altitude.
 *
 * Data is pushed by the server over SSE, never polled. The stream is opened
 * for the selected device only and filtered server-side, so another device's
 * pings never reach this page; `merge` re-checks the serial as a second guard.
 * History comes from one REST call on open and after any stream gap.
 */
export default function AdminBleLivePage(): ReactElement {
  const userEmail = useMemo(() => getUserNameFromSessionStorage(), []);
  const isAllowed = userEmail.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const [deviceList, setDeviceList] = useState<BleDevice[] | null>(null);
  const [serial, setSerial] = useState<number | null>(null);
  const [windowMin, setWindowMin] = useState(15);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const [version, setVersion] = useState(0);

  // History ("go back in time"): a fixed past range, loaded once per change.
  const [mode, setMode] = useState<Mode>('live');
  const [preset, setPreset] = useState<string>('1h');
  const [range, setRange] = useState<{ from: number; to: number } | null>(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [history, setHistory] = useState<HistoryData | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);

  // Rows live in a ref, not state: they change at stream rate, and the page
  // only re-renders on the throttled `version` tick.
  const rows = useRef<BlePing[]>([]);
  const seen = useRef<Set<number>>(new Set());
  const renderTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const serialRef = useRef(serial);
  serialRef.current = serial;

  const loadDevices = useCallback(async () => {
    try {
      const res = await axiosInstance.get<BleDevice[]>(`${BASE}/devices`);
      const list = Array.isArray(res.data) ? res.data : [];
      setDeviceList(list);
      // Default to the most recently active device.
      setSerial((current) => current ?? (list[0] ? list[0].deviceSerial : null));
    } catch {
      setDeviceList([]);
    }
  }, []);

  useEffect(() => {
    if (isAllowed) loadDevices();
  }, [isAllowed, loadDevices]);

  const requestRender = useCallback(() => {
    if (renderTimer.current || pausedRef.current) return;
    renderTimer.current = setTimeout(() => {
      renderTimer.current = null;
      setVersion((v) => v + 1);
    }, RENDER_INTERVAL_MS);
  }, []);

  const merge = useCallback(
    (incoming: BlePing[]) => {
      let added = false;
      let outOfOrder = false;
      const list = rows.current;
      incoming.forEach((p) => {
        // Second guard behind the server-side filter: only the selected device.
        if (!p || p.deviceSerial !== serialRef.current) return;
        if (seen.current.has(p.id) || !isNum(p.observedAt)) return;
        seen.current.add(p.id);
        // Phones upload in batches, so a ping can arrive after newer ones.
        if (list.length > 0 && p.observedAt < list[list.length - 1].observedAt) outOfOrder = true;
        list.push(p);
        added = true;
      });
      if (!added) return;
      if (outOfOrder) list.sort((a, b) => a.observedAt - b.observedAt);
      // Keep only the largest window, and never more than MAX_ROWS.
      const horizon = Date.now() - WINDOWS[WINDOWS.length - 1].key * 60000;
      let drop = 0;
      while (drop < list.length && list[drop].observedAt < horizon) drop += 1;
      drop = Math.max(drop, list.length - MAX_ROWS);
      if (drop > 0) list.splice(0, drop).forEach((p) => seen.current.delete(p.id));
      requestRender();
    },
    [requestRender]
  );

  const loadHistory = useCallback(async () => {
    if (serial === null) return;
    try {
      const res = await axiosInstance.get<BlePing[]>(`${BASE}/pings`, {
        params: { limit: HISTORY_LIMIT, offset: 0, serial }
      });
      if (Array.isArray(res.data)) merge(res.data);
    } catch {
      // The stream still works without history; the status pill shows trouble.
    }
  }, [serial, merge]);

  // One stream per selected device. Switching device starts from a clean slate.
  useEffect(() => {
    // History mode holds no stream open — browsing the past costs the server nothing.
    if (!isAllowed || serial === null || mode !== 'live') return undefined;
    rows.current = [];
    seen.current = new Set();
    setVersion((v) => v + 1);
    loadHistory();
    return openBleStream<BlePing>(String(serial), {
      onPings: merge,
      onStatus: setStatus,
      onGap: loadHistory
    });
  }, [isAllowed, serial, mode, merge, loadHistory]);

  // Scroll the time axis once a second even when nothing arrives. Local
  // redraw only — this never touches the network.
  useEffect(() => {
    if (paused || mode !== 'live') return undefined;
    const timer = setInterval(() => setVersion((v) => v + 1), 1000);
    return () => clearInterval(timer);
  }, [paused, mode]);

  const applyPreset = useCallback((key: string) => {
    const p = HISTORY_PRESETS.find((x) => x.key === key);
    if (!p) return;
    const to = Date.now();
    setPreset(key);
    setRange({ from: to - p.ms, to });
    setCustomFrom(toLocalInput(to - p.ms));
    setCustomTo(toLocalInput(to));
  }, []);

  const goHistory = useCallback(() => {
    setMode('history');
    if (!range) applyPreset(preset);
  }, [range, preset, applyPreset]);

  const applyCustom = useCallback(() => {
    const from = new Date(customFrom).getTime();
    const to = new Date(customTo).getTime();
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
      setHistoryError('Pick a start time before the end time.');
      return;
    }
    if (to - from > MAX_HISTORY_MS) {
      setHistoryError('A range can be at most 31 days.');
      return;
    }
    setPreset('custom');
    setRange({ from, to });
  }, [customFrom, customTo]);

  // One request per (device, range). The server does the downsampling, so the
  // payload stays around a thousand points however long the range is.
  useEffect(() => {
    if (!isAllowed || mode !== 'history' || serial === null || !range) return undefined;
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    axiosInstance
      .get<HistoryData>(`${BASE}/history`, { params: { serial, from: range.from, to: range.to } })
      .then((res) => {
        if (!cancelled) setHistory(res.data);
      })
      .catch((e: any) => {
        if (cancelled) return;
        setHistory(null);
        setHistoryError(
          e?.response?.status === 400 ? 'That range is not valid.' : 'Could not load history.'
        );
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAllowed, mode, serial, range]);

  useEffect(
    () => () => {
      if (renderTimer.current) clearTimeout(renderTimer.current);
    },
    []
  );

  const liveView = useMemo(() => {
    const now = Date.now();
    const windowMs = windowMin * 60000;
    const inWindow = rows.current.filter((p) => p.observedAt >= now - windowMs);

    const keys = new Set<string>();
    inWindow.forEach((p) => {
      if (p.values) Object.keys(p.values).forEach((k) => isNum(p.values?.[k]) && keys.add(k));
    });
    const parameters = Array.from(keys)
      .sort()
      .map((key, i) => {
        const color = PALETTE[i % PALETTE.length];
        const pts = points(inWindow, (p) => {
          const v = p.values?.[key];
          return isNum(v) ? v : null;
        });
        const latest = pts.length > 0 ? pts[pts.length - 1].y : null;
        return {
          key,
          color,
          series: [{ label: key, color, points: pts }] as LiveSeries[],
          stats: computeStats(
            pts.map((pt) => pt.y),
            latest
          )
        };
      });

    const magnitude = (p: BlePing): number | null =>
      isNum(p.accelX) && isNum(p.accelY) && isNum(p.accelZ)
        ? Math.sqrt(p.accelX ** 2 + p.accelY ** 2 + p.accelZ ** 2)
        : null;
    const accel: LiveSeries[] = [
      { label: 'X', color: '#F04438', points: points(inWindow, (p) => p.accelX) },
      { label: 'Y', color: '#12B76A', points: points(inWindow, (p) => p.accelY) },
      { label: 'Z', color: '#2E90FA', points: points(inWindow, (p) => p.accelZ) },
      { label: '|a|', color: '#D0D5DD', points: points(inWindow, magnitude) }
    ];
    const altitude: LiveSeries[] = [
      { label: 'Altitude', color: '#7A5AF8', points: points(inWindow, (p) => p.altitude) }
    ];
    const tracks: Track[] =
      serial === null
        ? []
        : [
            {
              key: String(serial),
              label: String(serial),
              color: TRACK_COLOR,
              points: inWindow
                .filter((p) => isNum(p.latitude) && isNum(p.longitude))
                .map((p) => ({
                  lat: p.latitude as number,
                  lng: p.longitude as number,
                  t: p.observedAt
                }))
            }
          ];

    const last = inWindow[inWindow.length - 1];
    const perMinute = inWindow.filter((p) => p.observedAt >= now - 60000).length;
    return {
      windowMs,
      range: undefined as { from: number; to: number } | undefined,
      gapMs: 60000,
      dataExtent: null as { from: number; to: number } | null,
      tiles: [
        { label: 'Pings in window', value: inWindow.length.toLocaleString() },
        { label: 'Last minute', value: `${perMinute} pings` },
        { label: 'Latest', value: last ? ageLabel(now - last.observedAt) : '—' }
      ],
      parameters,
      accel,
      altitude,
      tracks
    };
    // `version` is the change signal for the ref-held rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version, windowMin, serial]);

  // Same shape as the live view, built from the server's downsampled range.
  const historyView = useMemo(() => {
    if (!history) return null;
    const half = history.bucketMs / 2;
    // Plot each average at the middle of its bucket.
    const series = (pick: (p: HistoryPoint) => number | null): LivePoint[] => {
      const out: LivePoint[] = [];
      history.points.forEach((p) => {
        const y = pick(p);
        if (isNum(y)) out.push({ x: p.t + half, y });
      });
      return out;
    };
    const parameters = Object.keys(history.stats)
      .sort()
      .map((key, i) => {
        const color = PALETTE[i % PALETTE.length];
        const pts = series((p) => (isNum(p.values?.[key]) ? p.values[key] : null));
        const s = history.stats[key];
        return {
          key,
          color,
          series: [{ label: key, color, points: pts }] as LiveSeries[],
          // The diamond marks the last reading of the range.
          stats: { ...s, latest: pts.length > 0 ? pts[pts.length - 1].y : s.p50 } as ParameterStats
        };
      });
    const first = history.points[0];
    const last = history.points[history.points.length - 1];
    return {
      windowMs: history.to - history.from,
      range: { from: history.from, to: history.to },
      // Join neighbouring buckets; break only across 3+ empty buckets.
      gapMs: Math.max(60000, history.bucketMs * 3),
      dataExtent: first && last ? { from: first.t, to: last.t + history.bucketMs } : null,
      tiles: [
        { label: 'Pings in range', value: history.rawCount.toLocaleString() },
        { label: 'Span', value: spanLabel(history.to - history.from) },
        { label: 'Each point', value: `avg of ${bucketLabel(history.bucketMs)}` }
      ],
      parameters,
      accel: [
        { label: 'X', color: '#F04438', points: series((p) => p.accelX) },
        { label: 'Y', color: '#12B76A', points: series((p) => p.accelY) },
        { label: 'Z', color: '#2E90FA', points: series((p) => p.accelZ) },
        { label: '|a| peak', color: '#D0D5DD', points: series((p) => p.accelPeak) }
      ] as LiveSeries[],
      altitude: [
        { label: 'Altitude', color: '#7A5AF8', points: series((p) => p.altitude) }
      ] as LiveSeries[],
      tracks: [
        {
          key: String(history.deviceSerial),
          label: String(history.deviceSerial),
          color: TRACK_COLOR,
          points: history.points
            .filter((p) => isNum(p.latitude) && isNum(p.longitude))
            .map((p) => ({ lat: p.latitude as number, lng: p.longitude as number, t: p.t }))
        }
      ] as Track[]
    };
  }, [history]);

  const view =
    mode === 'history'
      ? historyView ?? {
          ...liveView,
          range: range ?? undefined,
          tiles: [],
          parameters: [],
          accel: [],
          altitude: [],
          tracks: []
        }
      : liveView;

  if (!isAllowed) {
    return <Navigate to="/" replace />;
  }

  const pill = statusPill(status);
  const selected = deviceList?.find((d) => d.deviceSerial === serial);
  const shownParameters = view.parameters.filter((p) => !hidden.has(p.key));
  const toggleParam = (key: string): void =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="flex flex-1 flex-col gap-4 px-6 py-6 text-gray-100">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-semibold">BLE Live Graphs</h1>
            {serial !== null && mode === 'history' && (
              <span className="rounded-full border border-primary-500/50 bg-primary-500/15 px-2.5 py-0.5 text-xs font-medium text-primary-400">
                History
              </span>
            )}
            {serial !== null && mode === 'live' && (
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${pill.className}`}
              >
                {status === 'live' && (
                  <span className="mr-1.5 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-current align-middle" />
                )}
                {pill.label}
              </span>
            )}
          </div>
          <p className="text-base text-gray-400">
            {selected
              ? `${selected.deviceName || 'Device'} · serial ${selected.deviceSerial}`
              : 'No device selected'}
            {' · '}
            <Link to="/admin/ble-pings" className="text-primary-400 hover:underline">
              raw table
            </Link>
          </p>
        </div>
        <div className="flex gap-3 text-base">
          {view.tiles.map((t) => (
            <div
              key={t.label}
              className="rounded border border-gray-800 bg-container-500 px-4 py-2"
            >
              <div className="text-sm text-gray-400">{t.label}</div>
              <div className="text-lg font-semibold">{t.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className={`flex flex-col gap-3 ${CARD_CLASS}`}>
        <div className="flex flex-wrap items-center gap-3">
          <label
            className="flex items-center gap-2 text-base text-gray-400"
            htmlFor="ble-live-serial"
          >
            Device
            <select
              id="ble-live-serial"
              value={serial ?? ''}
              onChange={(e) => setSerial(e.target.value ? Number(e.target.value) : null)}
              className="rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-base text-gray-100 focus:border-primary-500 focus:outline-none"
            >
              {(deviceList ?? []).length === 0 && <option value="">No devices yet</option>}
              {(deviceList ?? []).map((d) => (
                <option key={d.deviceSerial} value={d.deviceSerial}>
                  {d.deviceName || 'Device'} ({d.deviceSerial}) ·{' '}
                  {ageLabel(Date.now() - d.lastSeenAt)}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={loadDevices}
            title="Reload the device list"
            className="flex items-center rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-gray-300 transition hover:bg-background-500"
          >
            <span className="material-icons-outlined text-base">refresh</span>
          </button>
          <div className="flex overflow-hidden rounded border border-gray-800">
            {(['live', 'history'] as Mode[]).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => (m === 'live' ? setMode('live') : goHistory())}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm transition ${
                  mode === m
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'bg-background-700 text-gray-400 hover:text-gray-200'
                }`}
              >
                <span className="material-icons-outlined text-base">
                  {m === 'live' ? 'sensors' : 'history'}
                </span>
                {m === 'live' ? 'Live' : 'History'}
              </button>
            ))}
          </div>
          {mode === 'live' && (
            <>
              <div className="flex items-center gap-1">
                {WINDOWS.map((w) => (
                  <button
                    key={w.key}
                    type="button"
                    onClick={() => setWindowMin(w.key)}
                    className={`rounded border px-2.5 py-1.5 text-sm transition ${
                      windowMin === w.key
                        ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                        : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setPaused((p) => !p)}
                className="flex items-center gap-1.5 rounded border border-gray-800 bg-background-700 px-3 py-1.5 text-sm text-gray-200 transition hover:bg-background-500"
                title="Freeze the graphs to inspect them; pings keep buffering"
              >
                <span className="material-icons-outlined text-base">
                  {paused ? 'play_arrow' : 'pause'}
                </span>
                {paused ? 'Resume' : 'Pause'}
              </button>
            </>
          )}
          {mode === 'history' && (
            <>
              <div className="flex items-center gap-1">
                {HISTORY_PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => applyPreset(p.key)}
                    className={`rounded border px-2.5 py-1.5 text-sm transition ${
                      preset === p.key
                        ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                        : 'border-gray-800 bg-background-700 text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-400">
                <input
                  type="datetime-local"
                  aria-label="From"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-gray-100 [color-scheme:dark] focus:border-primary-500 focus:outline-none"
                />
                <span>to</span>
                <input
                  type="datetime-local"
                  aria-label="To"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="rounded border border-gray-800 bg-background-700 px-2 py-1.5 text-gray-100 [color-scheme:dark] focus:border-primary-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={applyCustom}
                  className={`rounded border px-3 py-1.5 transition ${
                    preset === 'custom'
                      ? 'border-primary-500/60 bg-primary-500/20 text-primary-400'
                      : 'border-gray-800 bg-background-700 text-gray-200 hover:bg-background-500'
                  }`}
                >
                  Show range
                </button>
                {view.dataExtent &&
                  view.range &&
                  view.dataExtent.to - view.dataExtent.from <
                    (view.range.to - view.range.from) / 2 && (
                    <button
                      type="button"
                      onClick={() => {
                        const ext = view.dataExtent as { from: number; to: number };
                        // A little margin either side, and at least a minute.
                        const pad = Math.max(30000, (ext.to - ext.from) * 0.05);
                        setCustomFrom(toLocalInput(ext.from - pad));
                        setCustomTo(toLocalInput(ext.to + pad));
                        setPreset('custom');
                        setRange({ from: ext.from - pad, to: ext.to + pad });
                      }}
                      title="This device only has data in part of the range — narrow to it for full detail"
                      className="flex items-center gap-1 rounded border border-success-500/40 bg-success-500/15 px-3 py-1.5 text-success-400 transition hover:bg-success-500/25"
                    >
                      <span className="material-icons-outlined text-base">zoom_in</span>
                      Zoom to data
                    </button>
                  )}
                {historyLoading && (
                  <span className="material-icons-outlined animate-spin text-base text-gray-400">
                    autorenew
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {view.parameters.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-sm text-gray-400">Parameters</span>
            {view.parameters.map((p) => {
              const on = !hidden.has(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => toggleParam(p.key)}
                  className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition ${
                    on
                      ? 'border-gray-600 bg-background-700 text-gray-100'
                      : 'border-gray-800 text-gray-500 hover:text-gray-300'
                  }`}
                >
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: on ? p.color : '#475467' }}
                  />
                  {p.key.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {mode === 'history' && historyError && (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          {historyError}
        </div>
      )}
      {mode === 'history' && history && history.rawCount === 0 && !historyLoading && (
        <div className={`${CARD_CLASS} text-gray-400`}>
          This device sent no pings between {new Date(history.from).toLocaleString()} and{' '}
          {new Date(history.to).toLocaleString()}.
        </div>
      )}
      {mode === 'live' && status === 'denied' && (
        <div className="rounded border border-error-500/40 bg-error-500/10 px-4 py-3 text-error-400">
          The server refused the live stream. Sign in again as the BLE admin.
        </div>
      )}
      {deviceList !== null && deviceList.length === 0 && (
        <div className={`${CARD_CLASS} text-gray-400`}>
          No device has sent BLE pings yet. Start the app (or the simulator) and press refresh.
        </div>
      )}

      {serial !== null && (
        <>
          <h2 className="text-lg font-semibold text-gray-200">Sensor parameters</h2>
          {shownParameters.length === 0 ? (
            <div className={`${CARD_CLASS} text-gray-400`}>
              {view.parameters.length === 0
                ? 'Waiting for readings from this device…'
                : 'All parameters are hidden — pick one above.'}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              {shownParameters.map((p) => (
                <div key={p.key} className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="text-base font-semibold capitalize text-gray-100">
                      {p.key.replace(/_/g, ' ')}
                    </h3>
                    {p.stats && (
                      <div className="flex items-baseline gap-4 text-right">
                        <span className="text-xs text-gray-400">
                          mean {fmt(p.stats.mean)} · min {fmt(p.stats.min)} · max {fmt(p.stats.max)}{' '}
                          · n {p.stats.count}
                        </span>
                        <span className="text-2xl font-semibold" style={{ color: p.color }}>
                          {fmt(p.stats.latest)}
                        </span>
                      </div>
                    )}
                  </div>
                  <LiveLineChart
                    series={p.series}
                    windowMs={view.windowMs}
                    range={view.range}
                    gapMs={view.gapMs}
                    version={version}
                  />
                  <ParameterBoxPlot stats={p.stats} color={p.color} />
                </div>
              ))}
            </div>
          )}

          <h2 className="mt-2 text-lg font-semibold text-gray-200">Phone context</h2>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <LiveTrackMap tracks={view.tracks} version={version} />
            <div className="flex min-w-0 flex-col gap-4">
              <div className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold text-gray-100">Accelerometer</h3>
                  <span className="text-xs text-gray-500">g · |a| ≈ 1 at rest</span>
                </div>
                <LiveLineChart
                  series={view.accel}
                  windowMs={view.windowMs}
                  range={view.range}
                  gapMs={view.gapMs}
                  version={version}
                  unit="g"
                  height="h-40"
                />
              </div>
              <div className={`flex min-w-0 flex-col gap-2 ${CARD_CLASS}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold text-gray-100">Altitude</h3>
                  <span className="text-xs text-gray-500">m</span>
                </div>
                <LiveLineChart
                  series={view.altitude}
                  windowMs={view.windowMs}
                  range={view.range}
                  gapMs={view.gapMs}
                  version={version}
                  unit="m"
                  height="h-40"
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
